import React from 'react';
import { motion } from 'framer-motion';
import type { Aggregates, AreaId } from '../../../types/pulse';
import { followUps, snapshot } from '../../../data/pulse';
import { followUpRows, rankFirstRows, ratingStats, sortedThemes } from '../../../utils/pulse';
import { AREA_BAR, AREA_BORDER, AREA_SOFT } from '../colors';
interface SnapshotScreenProps {
  aggregates: Aggregates;
  onRestart: () => void;
  onOpenData: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function SnapshotScreen({
  aggregates,
  onRestart,
  onOpenData
}: SnapshotScreenProps) {
  const leadingArea = rankFirstRows(aggregates, null)[0];
  const areaId = leadingArea.id as AreaId;
  const leadingPriority = followUpRows(aggregates, areaId, null).filter(r => r.id !== 'other')[0];
  const team = ratingStats(aggregates.team);
  const tech = ratingStats(aggregates.tech);
  const theme = sortedThemes(aggregates)[0];
  const rows = [{
    label: snapshot.rows.priority,
    value: leadingPriority.label,
    dot: AREA_BAR[areaId]
  }, {
    label: snapshot.rows.team,
    value: `${team.average} / 5`,
    dot: 'bg-teal'
  }, {
    label: snapshot.rows.tech,
    value: `${tech.average} / 5`,
    dot: 'bg-blue'
  }, {
    label: snapshot.rows.theme,
    value: theme.label,
    dot: 'bg-plum'
  }];
  return <div className="flex flex-1 flex-col" data-id="element-281" data-mp="3eh4ugktmzlcl">
      <motion.h2 initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="font-display text-[24px] font-semibold leading-[1.08] tracking-tight text-navy" data-id="element-282" data-mp="3ifm1z5gyyxuj">
        {snapshot.headline}
      </motion.h2>

      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      delay: 0.06,
      ease: EASE
    }} className={`mt-5 rounded-xl border px-4 py-4 ${AREA_SOFT[areaId]} ${AREA_BORDER[areaId]}`} data-id="element-283" data-mp="2urkpjlgo6x9u">
        <p className="text-[9px] uppercase tracking-micro text-navy2" data-id="element-284" data-mp="2qp7sdlfzo7oo">{snapshot.rows.area}</p>
        <p className="mt-1.5 font-display text-[26px] font-bold leading-none tracking-tight text-navy" data-id="element-285" data-mp="w8zcegykjcir">
          {leadingArea.label}
        </p>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-paper" data-id="element-286" data-mp="rh7u0tpkgd3p">
          <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: leadingArea.pct / 100
        }} transition={{
          duration: 0.3,
          delay: 0.14,
          ease: EASE
        }} style={{
          transformOrigin: 'left'
        }} className={`h-full ${AREA_BAR[areaId]}`} data-id="element-287" data-mp="miei35pxgu5n" />
        </div>
        <p className="mt-2 text-[11px] leading-snug text-navy2" data-id="element-288" data-mp="1a9tob9ktrvm6">
          <span className="font-semibold tabular-nums text-navy" data-id="element-289" data-mp="1675cqu4q8ali">{leadingArea.pct}%</span> of NOK
          delegates so far rank it #1.
        </p>
      </motion.div>

      <dl className="mt-4 divide-y divide-line border-y border-line" data-id="element-290" data-mp="117stskgz7no7">
        {rows.map((row, i) => <motion.div key={row.label} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.22,
        delay: 0.12 + i * 0.05,
        ease: EASE
      }} className="flex items-baseline justify-between gap-3 py-2.5" data-id="element-291" data-mp="3qvbajeby9hbd" data-mpi={i}>
            <dt className="flex min-w-0 flex-1 items-baseline gap-2 text-[11.5px] leading-snug text-mut" data-id="element-292" data-mp="26d967j6516ni">
              <span className={`h-2 w-2 shrink-0 rounded-sm ${row.dot}`} aria-hidden="true" data-id="element-293" data-mp="2af456h0lj7y4" />
              <span className="min-w-0 flex-1" data-id="element-294" data-mp="2f9rxkqyq0mhz">{row.label}</span>
            </dt>
            <dd className="shrink-0 text-right font-display text-[13px] font-semibold leading-snug tabular-nums text-navy" data-id="element-295" data-mp="2k68n9plkj8x6">
              {row.value}
            </dd>
          </motion.div>)}
      </dl>

      <p className="mt-5 text-[12.5px] leading-relaxed text-navy" data-id="element-296" data-mp="2u5pr01h9fcqi">
        {snapshot.closing[0]}
        <br data-id="element-297" data-mp="2y5qaw1n4og4y" />
        {snapshot.closing[1]}
      </p>

      <div className="mt-auto pt-6" data-id="element-298" data-mp="zroqzd9z4jou">
        <button type="button" onClick={onOpenData} className="mb-2 flex h-11 w-full items-center justify-center rounded-full bg-navy text-[13px] font-semibold text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-299" data-mp="zsq37l2q7c85">
          See all live results
        </button>
        <button type="button" onClick={onRestart} className="flex h-10 w-full items-center justify-center rounded-full border border-line text-[12px] font-medium text-navy transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-300" data-mp="ifq9pr8ew92v">
          {snapshot.restart}
        </button>
        <p className="mt-3 text-[9px] uppercase tracking-micro text-mut" data-id="element-301" data-mp="3vo31uxhve5m3">{snapshot.footer}</p>
      </div>
    </div>;
}